import { TeamIcon, TellFilledIcon, theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  FilterDrawer,
  QuestionFilterContent,
  TeamFilterContent,
  useFilterDrawer,
  useQuestionExpansion,
  useTeamExpansion,
  type AnswerSelection,
  type QuestionItem,
  type TeamNode,
} from './FilterDrawer';

interface ReportFiltersProps {
  teams: TeamNode[];
  questions: QuestionItem[];
  selectedTeams: Set<string>;
  onSelectedTeamsChange: (teams: Set<string>) => void;
  selectedAnswers: AnswerSelection[];
  onSelectedAnswersChange: (answers: AnswerSelection[]) => void;
  compact?: boolean;
}

export const ReportFilters = ({
  teams,
  questions,
  selectedTeams,
  onSelectedTeamsChange,
  selectedAnswers,
  onSelectedAnswersChange,
  compact,
}: ReportFiltersProps) => {
  const { t } = useTranslation('newReport');

  const teamDrawer = useFilterDrawer<Set<string>>({
    value: selectedTeams,
    onConfirm: onSelectedTeamsChange,
    isEmpty: (v) => v.size === 0,
    emptyValue: new Set(),
  });

  const questionDrawer = useFilterDrawer<AnswerSelection[]>({
    value: selectedAnswers,
    onConfirm: onSelectedAnswersChange,
    isEmpty: (v) => v.length === 0,
    emptyValue: [],
  });

  const teamExpansion = useTeamExpansion(teams);
  const questionExpansion = useQuestionExpansion(questions);

  const [teamSearch, setTeamSearch] = useState('');
  const [questionSearch, setQuestionSearch] = useState('');

  useEffect(() => {
    if (teamSearch) {
      teamExpansion.expandAllForSearch(teams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamSearch, teams]);

  useEffect(() => {
    if (questionSearch) {
      questionExpansion.expandAllForSearch(questions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionSearch, questions]);

  const handleToggleTeam = useCallback(
    (ids: string[]) => {
      teamDrawer.setDraftValue((prev) => {
        const next = new Set(prev);
        const allSelected = ids.every((id) => next.has(id));
        for (const id of ids) {
          if (allSelected) {
            next.delete(id);
          } else {
            next.add(id);
          }
        }
        return next;
      });
    },
    [teamDrawer],
  );

  const handleToggleAnswer = useCallback(
    (questionId: string, optionId: string, statementId?: string) => {
      questionDrawer.setDraftValue((prev) => {
        const exists = prev.some(
          (a) => a.questionId === questionId && a.optionId === optionId && a.statementId === statementId,
        );
        if (exists) {
          return prev.filter(
            (a) => !(a.questionId === questionId && a.optionId === optionId && a.statementId === statementId),
          );
        }
        return [...prev, { questionId, optionId, statementId }];
      });
    },
    [questionDrawer],
  );

  return (
    <>
      <FilterButtons compact={compact}>
        <Button onClick={teamDrawer.open} style={filterButtonStyle} size="sm">
          <StyledTeamIcon />
          {t('filterTeams')} {selectedTeams.size > 0 && `(${selectedTeams.size})`}
        </Button>
        <Button onClick={questionDrawer.open} style={filterButtonStyle} size="sm">
          <StyledQuestionIcon />
          {t('filterQuestions')} {selectedAnswers.length > 0 && `(${selectedAnswers.length})`}
        </Button>
      </FilterButtons>

      <FilterDrawer
        open={teamDrawer.isOpen}
        onClose={teamDrawer.close}
        onConfirm={teamDrawer.confirm}
        onReset={teamDrawer.reset}
        title={t('filterTeams')}
        hasActiveFilters={teamDrawer.hasActiveFilters}
        searchPlaceholder={t('searchPlaceholderTeams')}
        searchValue={teamSearch}
        onSearchChange={setTeamSearch}
        showExpandCollapse
        allExpanded={teamExpansion.allExpanded}
        onToggleExpandAll={teamExpansion.toggleExpandAll}
      >
        <TeamFilterContent
          teams={teams}
          selectedTeamIds={teamDrawer.draftValue}
          onToggleTeam={handleToggleTeam}
          searchQuery={teamSearch}
          expandedIds={teamExpansion.expandedIds}
          onToggleExpand={teamExpansion.toggleExpand}
        />
      </FilterDrawer>

      <FilterDrawer
        open={questionDrawer.isOpen}
        onClose={questionDrawer.close}
        onConfirm={questionDrawer.confirm}
        onReset={questionDrawer.reset}
        title={t('filterQuestionsAndAnswers')}
        hasActiveFilters={questionDrawer.hasActiveFilters}
        searchPlaceholder={t('searchPlaceholderQuestionsAndAnswers')}
        searchValue={questionSearch}
        onSearchChange={setQuestionSearch}
        showExpandCollapse
        allExpanded={questionExpansion.allExpanded}
        onToggleExpandAll={questionExpansion.toggleExpandAll}
      >
        <QuestionFilterContent
          questions={questions}
          selectedAnswers={questionDrawer.draftValue}
          onToggleAnswer={handleToggleAnswer}
          searchQuery={questionSearch}
          expandedIds={questionExpansion.expandedIds}
          onToggleExpand={questionExpansion.toggleExpand}
        />
      </FilterDrawer>
    </>
  );
};

const filterButtonStyle = {
  padding: `${theme.spacing.d} ${theme.spacing.e}`,
  border: `${theme.spacing.a} solid ${theme.colors.brand.primary}`,
  borderRadius: theme.spacing.d,
  background: 'white',
  cursor: 'pointer',
  color: theme.colors.brand.primary,
};

const FilterButtons = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.f};
  margin-top: ${(props) => (props.compact ? theme.spacing.f : theme.spacing.g)};
`;

const StyledTeamIcon = styled(TeamIcon)`
  margin-right: ${theme.spacing.d};
  path {
    fill: ${theme.colors.brand.primary};
  }
`;

const StyledQuestionIcon = styled(TellFilledIcon)`
  fill: ${theme.colors.brand.primary};
  margin-right: ${theme.spacing.d};
`;
